import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Knowledge.css';

interface UnansweredQuestion {
  id: number;
  question: string;
  customer_name: string | null;
  created_at: string;
}

interface KnowledgeArticle {
  id: number;
  question: string;
  times_used: number;
  category: string;
}

const KnowledgeDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    totalArticles: 0,
    pendingQuestions: 0,
    documentsUploaded: 0,
  });
  const [recentQuestions, setRecentQuestions] = useState<UnansweredQuestion[]>([]);
  const [topArticles, setTopArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [articlesRes, questionsRes, docsRes] = await Promise.all([
        api.get('/knowledge/articles/'),
        api.get('/knowledge/questions/pending/'),
        api.get('/knowledge/documents/'),
      ]);

      setStats({
        totalArticles: articlesRes.data.length || articlesRes.data.count || 0,
        pendingQuestions: questionsRes.data.length || 0,
        documentsUploaded: docsRes.data.length || docsRes.data.count || 0,
      });

      setRecentQuestions(questionsRes.data.slice(0, 5));

      // Sort by times_used and get top 5
      const sorted = [...(articlesRes.data.results || articlesRes.data)]
        .sort((a: KnowledgeArticle, b: KnowledgeArticle) => b.times_used - a.times_used);
      setTopArticles(sorted.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="knowledge-container"><div className="knowledge-loading">Loading...</div></div>;
  }

  return (
    <div className="knowledge-container">
      <div className="knowledge-header">
        <h1>Knowledge Base</h1>
        <p className="knowledge-subtitle">Train Hank & Laura to answer customer questions better</p>
      </div>

      {/* Stats Cards */}
      <div className="knowledge-stats-grid">
        <div className="knowledge-stat-card stat-pending">
          <div className="stat-value">{stats.pendingQuestions}</div>
          <div className="stat-label">Pending Questions</div>
          <Link to="/knowledge/pending" className="stat-link">
            Review now
          </Link>
        </div>

        <div className="knowledge-stat-card stat-articles">
          <div className="stat-value">{stats.totalArticles}</div>
          <div className="stat-label">Knowledge Articles</div>
          <Link to="/knowledge/articles" className="stat-link">
            Browse
          </Link>
        </div>

        <div className="knowledge-stat-card stat-documents">
          <div className="stat-value">{stats.documentsUploaded}</div>
          <div className="stat-label">Documents</div>
          <Link to="/knowledge/documents" className="stat-link">
            Manage
          </Link>
        </div>
      </div>

      <div className="knowledge-panels-grid">
        {/* Recent Unanswered Questions */}
        <div className="knowledge-panel">
          <div className="panel-header">
            <h2>Needs Answers</h2>
            <Link to="/knowledge/pending" className="panel-link">View all</Link>
          </div>
          <div className="panel-content">
            {recentQuestions.length === 0 ? (
              <div className="panel-empty">
                <span className="panel-empty-icon">&#127881;</span>
                <p>All caught up!</p>
              </div>
            ) : (
              <ul className="question-list">
                {recentQuestions.map((q) => (
                  <li key={q.id} className="question-item">
                    <p className="question-text">{q.question}</p>
                    <p className="question-meta">
                      {q.customer_name || 'Unknown customer'} &bull; {new Date(q.created_at).toLocaleDateString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Top Used Articles */}
        <div className="knowledge-panel">
          <div className="panel-header">
            <h2>Most Used Articles</h2>
            <Link to="/knowledge/articles" className="panel-link">View all</Link>
          </div>
          <div className="panel-content">
            {topArticles.length === 0 ? (
              <div className="panel-empty">
                <p>No articles yet</p>
              </div>
            ) : (
              <ul className="article-list">
                {topArticles.map((a) => (
                  <li key={a.id} className="article-item">
                    <span className="article-text">{a.question}</span>
                    <span className="article-usage">{a.times_used} uses</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="knowledge-actions">
        <h2>Quick Add Knowledge</h2>
        <div className="action-buttons">
          <Link to="/knowledge/articles/new" className="btn-action btn-green">
            + Add New Article
          </Link>
          <Link to="/knowledge/documents" className="btn-action btn-blue">
            Upload Document
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeDashboard;
